import {
  MailboxFolderRole as FolderRole,
  TriggerEmailAddressKind as AddressKind,
  TriggerEmailArrivalMode as Arrival,
  TriggerEmailPhraseField as PhraseField,
  TriggerEmailRecipientField as RecipientField,
  TriggerEmailRuleKind as Kind,
  TriggerEmailScopeMode as ScopeMode,
  TriggerEmailTextOperator as TextOperator,
  TriggerEmailTimeOperator as TimeOperator,
  TriggerEmailValueMode as ValueMode,
  type TriggerEmailWeekday as Weekday,
  type TriggerEmailAddressMatchInput,
  type TriggerEmailRuleInput,
  type TriggerEmailTextMatchInput
} from '~/api/graphql';
import {
  EMAIL_WEEKDAYS,
  isEmailFolderRole,
  type EmailConfigDraft,
  type EmailFolderRole
} from '~/api/data/triggers/email-config';

// Plain-words summaries of a connected-mailbox email trigger, for the trigger list,
// the trigger details and the review dialog. They describe a saved or draft config;
// they never invent data the config does not hold (folder IDs stay IDs unless the
// caller can name them).

/** One line of a rule summary; groups carry their members as children. */
export type EmailRuleLine = { text: string; children?: EmailRuleLine[] };

export type EmailSummaryNames = {
  /** Mailbox address for an account ID, when known. */
  mailbox?: (accountId: string) => string | undefined;
  /** Folder name for a provider folder ID in an account, when known. */
  folder?: (accountId: string, folderId: string) => string | undefined;
  /** Agency user name for a user ID, when known. */
  user?: (userId: string) => string | undefined;
  /** Access profile name for a profile ID, when known. */
  profile?: (profileId: string) => string | undefined;
};

const countLabel = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const quote = (value: string) => `“${value}”`;

// Decimal units, matching the size field's help text (1,000,000 bytes is about 1 MB).
const BYTES_PER_KB = 1_000;
const BYTES_PER_MB = 1_000_000;

const formatBytes = (bytes: number) =>
  bytes >= BYTES_PER_MB
    ? `${+(bytes / BYTES_PER_MB).toFixed(1)} MB`
    : bytes >= BYTES_PER_KB
      ? `${+(bytes / BYTES_PER_KB).toFixed(1)} KB`
      : `${bytes} byte${bytes === 1 ? '' : 's'}`;

const describeAttachmentSize = (min?: number | null, max?: number | null) => {
  const from = min != null && min > 0 ? min : null;
  if (from != null && max != null) return `sized ${formatBytes(from)} to ${formatBytes(max)}`;
  if (from != null) return `sized at least ${formatBytes(from)}`;
  if (max != null) return `sized at most ${formatBytes(max)}`;
  return '';
};

const joinValues = (values: string[], mode?: ValueMode | null) => {
  if (values.length <= 1) return values.join('');
  const word = mode === ValueMode.All ? 'and' : 'or';
  return `${values.slice(0, -1).join(', ')} ${word} ${values[values.length - 1]}`;
};

export const EMAIL_FOLDER_ROLE_LABELS: Record<EmailFolderRole, string> = {
  [FolderRole.Inbox]: 'Inbox',
  [FolderRole.Sent]: 'Sent Items',
  [FolderRole.Archive]: 'Archive',
  [FolderRole.Junk]: 'Junk Email',
  [FolderRole.Trash]: 'Trash'
};

/** A provider folder's display name: its path without a leading slash, else its name. */
export const folderLabel = (folder: { name: string; path?: string | null }) =>
  folder.path?.replace(/^\/+/, '') || folder.name;

/** Which mailboxes the trigger watches, e.g. "ops@agency.com" or "All connected mailboxes". */
export const describeEmailMailboxes = (
  config: Pick<EmailConfigDraft, 'scope'>,
  names: EmailSummaryNames = {}
): string => {
  if (config.scope.mode === ScopeMode.All) return 'All connected mailboxes';
  const labels = (config.scope.accounts ?? []).map(
    (account) => names.mailbox?.(account.accountId) ?? 'Connected mailbox'
  );
  if (labels.length === 0) return 'No mailbox selected';
  if (labels.length === 1) return labels[0];
  if (new Set(labels).size === 1) return `${labels.length} mailboxes`;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels[0]} and ${labels.length - 1} more mailboxes`;
};

/**
 * The watched mailboxes as they read after "An email arrives in". Watching every
 * mailbox also covers ones connected after the trigger was saved, so say so.
 */
export const describeEmailMailboxesInSentence = (
  config: Pick<EmailConfigDraft, 'scope'>,
  names: EmailSummaryNames = {}
): string =>
  config.scope.mode === ScopeMode.All
    ? 'any connected mailbox, including ones connected later'
    : describeEmailMailboxes(config, names);

const describeEverywhere = (includeJunkAndDeleted?: boolean | null) =>
  includeJunkAndDeleted
    ? 'All folders, including Junk Email and Trash'
    : 'All folders except Junk Email and Trash';

/** The folders the trigger watches, one label per folder or folder rule. */
export const describeEmailFolders = (
  config: Pick<EmailConfigDraft, 'scope'>,
  names: EmailSummaryNames = {}
): string[] => {
  if (config.scope.mode === ScopeMode.All) {
    const folders = config.scope.folders;
    if (!folders) return [];
    if (folders.allFolders) return [describeEverywhere(folders.includeJunkAndDeleted)];
    return [
      ...(folders.standard ?? []).map((role) =>
        isEmailFolderRole(role) ? EMAIL_FOLDER_ROLE_LABELS[role] : role
      ),
      ...(folders.paths ?? [])
    ];
  }
  const accounts = config.scope.accounts ?? [];
  const labels: string[] = [];
  for (const account of accounts) {
    if (account.folders.allFolders) {
      labels.push(describeEverywhere(account.folders.includeJunkAndDeleted));
      continue;
    }
    const ids = account.folders.ids ?? [];
    const named = ids.map((id) => names.folder?.(account.accountId, id));
    if (named.every((name): name is string => !!name)) labels.push(...named);
    else labels.push(countLabel(ids.length, 'selected folder'));
  }
  return [...new Set(labels)];
};

/** Never describe a stale or unsupported saved arrival mode as new mail. */
export const describeEmailArrivals = (config: Pick<EmailConfigDraft, 'arrivals'>) =>
  config.arrivals === Arrival.New ? 'New mail only' : 'Unsupported arrival mode';

const TEXT_OPERATOR_LABELS: Record<TextOperator, string> = {
  [TextOperator.Contains]: 'contains',
  [TextOperator.Equals]: 'is',
  [TextOperator.StartsWith]: 'starts with',
  [TextOperator.EndsWith]: 'ends with',
  [TextOperator.Regex]: 'matches the pattern'
};

const NEGATED_TEXT_OPERATOR_LABELS: Record<TextOperator, string> = {
  [TextOperator.Contains]: 'does not contain',
  [TextOperator.Equals]: 'is not',
  [TextOperator.StartsWith]: 'does not start with',
  [TextOperator.EndsWith]: 'does not end with',
  [TextOperator.Regex]: 'does not match the pattern'
};

// A negated condition is a NOT around one leaf; it reads as the negated operator the
// editor offered ("Does not contain"). NOT(any of A, B) means neither, which "does not
// contain A or B" says; NOT(all of A and B) needs "all of" to stay unambiguous.
const negatedValues = (values: string[], mode?: ValueMode | null) =>
  `${values.length > 1 && mode === ValueMode.All ? 'all of ' : ''}${joinValues(values, mode)}`;

const describeText = (match: TriggerEmailTextMatchInput, negated = false) =>
  `${
    (negated ? NEGATED_TEXT_OPERATOR_LABELS : TEXT_OPERATOR_LABELS)[match.operator] ??
    (negated ? 'does not match' : 'matches')
  } ${(negated ? negatedValues : joinValues)((match.values ?? []).map(quote), match.valueMode)}${
    match.caseSensitive ? ' (case-sensitive)' : ''
  }`;

const PHRASE_FIELD_LABELS: Record<PhraseField, string> = {
  [PhraseField.Subject]: 'Subject',
  [PhraseField.Body]: 'Body',
  [PhraseField.SubjectOrBody]: 'Subject or body'
};

const RECIPIENT_FIELD_LABELS: Record<RecipientField, string> = {
  [RecipientField.To]: 'To',
  [RecipientField.Cc]: 'Cc',
  [RecipientField.ToOrCc]: 'To or Cc'
};

const describeAddress = (
  match: TriggerEmailAddressMatchInput,
  names: EmailSummaryNames,
  negated = false
) => {
  const values = match.values ?? [];
  const is = negated ? 'is not' : 'is';
  const join = negated ? negatedValues : joinValues;
  switch (match.kind) {
    case AddressKind.Domain:
      return `${is} from ${join(values, match.valueMode)}${
        match.includeSubdomains ? ' (including subdomains)' : ''
      }`;
    case AddressKind.User:
      return `${is} ${join(
        values.map((id) => names.user?.(id) ?? 'an agency user'),
        match.valueMode
      )}`;
    case AddressKind.AccessProfile:
      return `${is} in ${join(
        values.map((id) => names.profile?.(id) ?? 'an access profile'),
        match.valueMode
      )}`;
    default:
      return `${is} ${join(values, match.valueMode)}`;
  }
};

const describeRecipient = (
  match: TriggerEmailAddressMatchInput,
  names: EmailSummaryNames,
  negated = false
) => {
  const values = match.values ?? [];
  const includes = negated ? 'does not include' : 'includes';
  const someone = negated ? 'anyone' : 'someone';
  const join = negated ? negatedValues : joinValues;
  switch (match.kind) {
    case AddressKind.Domain:
      return `${includes} ${someone} from ${join(values, match.valueMode)}${
        match.includeSubdomains ? ' (including subdomains)' : ''
      }`;
    case AddressKind.User:
      return `${includes} ${join(
        values.map((id) => names.user?.(id) ?? 'an agency user'),
        match.valueMode
      )}`;
    case AddressKind.AccessProfile:
      return `${includes} ${someone} in ${join(
        values.map((id) => names.profile?.(id) ?? 'an access profile'),
        match.valueMode
      )}`;
    default:
      return `${includes} ${join(values, match.valueMode)}`;
  }
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const formatEmailMinute = (minute: number | null | undefined) => {
  if (minute == null || !Number.isInteger(minute) || minute < 0 || minute > 1440)
    return '(time not set)';
  if (minute === 1440) return '12:00 AM (end of day)';
  const hours = Math.floor(minute / 60) % 24;
  const minutes = minute % 60;
  const suffix = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}`;
};

export const formatEmailWeekdays = (weekdays: Weekday[]) => {
  // Positions 1 (Monday) to 7 (Sunday); unknown names are left out.
  const days = [...new Set(weekdays.map((day) => EMAIL_WEEKDAYS.indexOf(day) + 1))]
    .filter((day) => day >= 1)
    .sort((a, b) => a - b);
  if (days.length === 7) return 'every day';
  // No days never matches (and cannot be saved); never describe it as every day.
  if (days.length === 0) return 'no days chosen';
  const ranges: string[] = [];
  let start = days[0];
  let previous = days[0];
  for (const day of [...days.slice(1), Number.NaN]) {
    if (day === previous + 1) {
      previous = day;
      continue;
    }
    const first = WEEKDAY_LABELS[start - 1];
    const last = WEEKDAY_LABELS[previous - 1];
    ranges.push(
      start === previous
        ? first
        : previous === start + 1
          ? `${first}, ${last}`
          : `${first} to ${last}`
    );
    start = day;
    previous = day;
  }
  return ranges.join(', ');
};

const describeLeaf = (
  rule: TriggerEmailRuleInput,
  names: EmailSummaryNames,
  negated = false
): string => {
  switch (rule.kind) {
    case Kind.Phrase:
      return rule.phrase
        ? `${PHRASE_FIELD_LABELS[rule.phrase.field] ?? 'Text'} ${describeText(
            rule.phrase.match,
            negated
          )}`
        : 'Text condition';
    case Kind.Sender:
      return rule.sender
        ? `Sender ${describeAddress(rule.sender, names, negated)}`
        : 'Sender condition';
    case Kind.Recipient:
      return rule.recipient
        ? `${RECIPIENT_FIELD_LABELS[rule.recipient.field] ?? 'Recipients'} ${describeRecipient(
            rule.recipient.match,
            names,
            negated
          )}`
        : 'Recipient condition';
    case Kind.Time: {
      const time = rule.time;
      if (!time) return 'Time condition';
      const days = formatEmailWeekdays(time.weekdays ?? []);
      const arrives = negated ? 'Does not arrive' : 'Arrives';
      if (time.operator === TimeOperator.Between)
        return `${arrives} between ${formatEmailMinute(time.minute)} and ${formatEmailMinute(
          time.endMinute
        )}, ${days}`;
      return `${arrives} ${time.operator === TimeOperator.Before ? 'before' : 'after'} ${formatEmailMinute(
        time.minute
      )}, ${days}`;
    }
    case Kind.Attachment: {
      const attachment = rule.attachment;
      if (!attachment) return 'Attachment condition';
      const min = attachment.minimumCount ?? 1;
      const max = attachment.maximumCount;
      const count =
        max == null
          ? `at least ${countLabel(min, 'attachment')}`
          : min === max
            ? `exactly ${countLabel(min, 'attachment')}`
            : `${min} to ${max} attachments`;
      const details = [
        attachment.name ? `whose name ${describeText(attachment.name)}` : '',
        (attachment.extensions ?? []).length
          ? `of type ${joinValues((attachment.extensions ?? []).map((ext) => `.${ext.replace(/^\./, '')}`))}`
          : '',
        (attachment.mimeTypes ?? []).length
          ? `with MIME type ${joinValues(attachment.mimeTypes ?? [])}`
          : '',
        describeAttachmentSize(attachment.minimumBytes, attachment.maximumBytes)
      ].filter(Boolean);
      const suffix = details.length ? ` ${details.join(', ')}` : '';
      if (!negated) return `Has ${count}${suffix}`;
      // NOT(at least N) is "fewer than N", which for N = 1 is "no attachments".
      if (max == null)
        return min <= 1
          ? `Has no attachments${suffix}`
          : `Has fewer than ${countLabel(min, 'attachment')}${suffix}`;
      return `Does not have ${count}${suffix}`;
    }
    case Kind.Prompt:
      if (negated)
        return rule.prompt ? `Cara answers no to ${quote(rule.prompt.prompt)}` : 'Cara answers no';
      return rule.prompt ? `Cara decides: ${quote(rule.prompt.prompt)}` : 'Cara decides';
    default:
      return 'Condition';
  }
};

const GROUP_LABELS: Partial<Record<Kind, string>> = {
  [Kind.And]: 'All of these',
  [Kind.Or]: 'Any of these',
  // Only reached for a NOT the editor never builds (e.g. NOT around a NOT).
  [Kind.Not]: 'Not'
};

/**
 * The IF conditions as nested lines. A top-level AND is flattened into its members,
 * since "all of these" is how a plain list reads; null means no extra conditions.
 */
export const describeEmailRule = (
  rule: TriggerEmailRuleInput | null | undefined,
  names: EmailSummaryNames = {}
): EmailRuleLine[] => {
  if (!rule) return [];
  const toLine = (node: TriggerEmailRuleInput): EmailRuleLine => {
    if (node.kind === Kind.Not && node.children?.length === 1) {
      const [child] = node.children;
      // The editor offers negation as an operator ("Does not contain"), stored as a NOT
      // around the one condition; say it that way rather than as a one-member group.
      if (!GROUP_LABELS[child.kind]) return { text: describeLeaf(child, names, true) };
      // NOT(OR) is "none of these"; NOT(AND) is "not all of these".
      if (child.kind === Kind.Or || child.kind === Kind.And)
        return {
          text: child.kind === Kind.Or ? 'None of these' : 'Not all of these',
          children: (child.children ?? []).map(toLine)
        };
    }
    const label = GROUP_LABELS[node.kind];
    if (!label) return { text: describeLeaf(node, names) };
    return { text: label, children: (node.children ?? []).map(toLine) };
  };
  if (rule.kind === Kind.And) return (rule.children ?? []).map(toLine);
  return [toLine(rule)];
};
